import {produce} from 'immer';

import {Action, EntryPart, QueryFormulaEntry} from 'common/query-builder/query-entry-section/types';
import {ResourceType} from 'common/report/constants';
import {KEY_VALUES} from 'common/util/dom';

import {BaseEntryRow} from '../base';
import {FormulaEntryRowEl} from './template';
import {getNextFormulaName} from './utils';

export class FormulaEntryRow extends BaseEntryRow<QueryFormulaEntry> {
  template = FormulaEntryRowEl;

  isPlaceholder(): boolean {
    const {label, value} = this.entry.event;
    return !(label && value);
  }

  static getNewEntry({existingFormulaNames}: {existingFormulaNames: string[]}): QueryFormulaEntry {
    return {
      type: ResourceType.Formula,
      event: {
        label: getNextFormulaName({existingFormulaNames}),
        value: null,
      },
      filters: [],
    };
  }

  onFormulaDefinitionCommit(newDefinition: string) {
    if (newDefinition) {
      const currentEntry = this.section.getEntry<QueryFormulaEntry>(this.index);
      this.section.updateEntries(Action.Edit, {
        entry: produce(currentEntry, (draft) => {
          draft.event.value = newDefinition;
        }),
        index: this.index,
      });
    } else if (!this.section.isStagedEntry(this.index)) {
      // remove the formula entry if its definition is edited to be the empty string.
      // the !isStagedEntry check is to limit this codepath to editing existing definition rather than newly
      // staged formula.
      this.section.updateEntries(Action.Remove, {index: this.index});
    }
    this.section.helpers.setActiveEntry(`close`, {index: this.index, part: EntryPart.Value});
  }

  handleDefinitionKeydown(ev: KeyboardEvent) {
    if (ev.key === KEY_VALUES.ENTER) {
      ev.preventDefault();
      const inputEl = ev.currentTarget as HTMLInputElement;
      inputEl.blur();
    }
  }

  handleDefinitionInput(ev: InputEvent) {
    const inputEl = ev.currentTarget as HTMLInputElement;
    inputEl.value = inputEl.value.toUpperCase();
  }

  handleDefinitionBlur(ev: FocusEvent) {
    this.onFormulaDefinitionCommit((ev.currentTarget as HTMLInputElement).value);
  }

  handleDefinitionFocus() {
    const {index: activeIndex, part: activePart} = this.section.state.openEntry;
    if (activeIndex === this.index && activePart === EntryPart.Value) {
      // The formula will already be openEntry if it was added as new formula and autofocused. In this
      // case, don't update to avoid an unnecessary re-render
      return;
    }
    this.section.helpers.setActiveEntry(`open`, {index: this.index, part: EntryPart.Value});
  }
}
